import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { colors, fonts } from '../brand';
import Disclaimer from '../components/Disclaimer';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import Accordion from '../components/Accordion';

export default function Glossary() {
  const [terms, setTerms] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/content/glossary`).then(res => setTerms(res.data.terms || [])).catch(() => {});
  }, []);

  const filtered = terms.filter(t =>
    t.term.toLowerCase().includes(search.toLowerCase()) ||
    t.definition.toLowerCase().includes(search.toLowerCase())
  );

  const ranges = [
    { label: 'A – C', start: 'A', end: 'C' },
    { label: 'D – F', start: 'D', end: 'F' },
    { label: 'G – I', start: 'G', end: 'I' },
    { label: 'J – L', start: 'J', end: 'L' },
    { label: 'M – O', start: 'M', end: 'O' },
    { label: 'P – R', start: 'P', end: 'R' },
    { label: 'S – U', start: 'S', end: 'U' },
    { label: 'V – Z', start: 'V', end: 'Z' },
  ];

  const accordionItems = ranges
    .map(range => {
      const items = filtered.filter(t => {
        const l = t.term[0].toUpperCase();
        return l >= range.start && l <= range.end;
      });
      return { label: range.label, items };
    })
    .filter(group => group.items.length > 0)
    .map(group => ({
      label: group.label,
      children: (
        <div>
          {group.items.map(item => (
            <div key={item.term} style={styles.entry}>
              <h2 style={styles.term}>{item.term}</h2>
              <p style={styles.definition}>{item.definition}</p>
            </div>
          ))}
        </div>
      ),
    }));

  return (
    <div style={styles.page}>
      <Helmet>
        <title>Divorce Glossary — Plain-Language Legal Terms | Elephants Answering</title>
        <meta name="description" content="Legal language shouldn't be a barrier. Browse divorce terms explained in plain English — from custody and discovery to QDRO and guardian ad litem." />
      </Helmet>
      <SiteNav />
      <main style={styles.main}>
        <div style={styles.inner}>
          <p style={styles.eyebrow}>FREE RESOURCE</p>
          <h1 style={styles.title}>Divorce Glossary</h1>
          <p style={styles.subtitle}>
            Legal language shouldn't be a barrier. Here are the terms you'll encounter — explained in plain English.
          </p>
          <input
            type="text"
            placeholder="Search terms..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={styles.search}
          />
          {filtered.length === 0 ? (
            <p style={styles.noResults}>No terms match your search.</p>
          ) : (
            <Accordion items={accordionItems} allowMultiple={true} defaultOpen={search ? 'all' : null} />
          )}
          <div style={styles.cta}>
            <p style={styles.ctaText}>Still have questions? Join women who've been through it.</p>
            <Link to="/register" style={styles.ctaBtn}>Join the Community</Link>
            <p style={styles.ctaTagline}>Free for Early Members</p>
          </div>
        </div>
      </main>
      <Disclaimer />
      <SiteFooter />
    </div>
  );
}

const styles = {
  page: { fontFamily: fonts.body, backgroundColor: colors.cream, minHeight: '100vh' },
  main: { padding: '64px 20px 80px' },
  inner: { maxWidth: '780px', margin: '0 auto' },
  eyebrow: { fontFamily: fonts.body, color: colors.rose, fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '12px' },
  title: { fontFamily: fonts.heading, color: colors.navy, fontSize: '36px', fontWeight: '600', marginBottom: '16px', letterSpacing: '0.3px' },
  subtitle: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '15px', lineHeight: '1.9', marginBottom: '36px' },
  search: { width: '100%', padding: '12px 16px', fontSize: '14px', fontFamily: fonts.body, border: `1px solid ${colors.peach}`, outline: 'none', borderRadius: '2px', backgroundColor: colors.white, color: colors.navy, marginBottom: '40px', boxSizing: 'border-box' },
  noResults: { color: colors.textMedium, fontStyle: 'italic', fontSize: '14px' },
  entry: { borderBottom: `1px solid ${colors.peach}`, padding: '28px 0' },
  term: { fontFamily: fonts.heading, color: colors.navy, fontSize: '16px', fontWeight: '600', marginBottom: '8px', letterSpacing: '0.3px' },
  definition: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', lineHeight: '1.9', margin: 0 },
  cta: { marginTop: '64px', padding: '48px 40px', backgroundColor: colors.navy, textAlign: 'center' },
  ctaText: { fontFamily: fonts.heading, color: colors.white, fontSize: '18px', marginBottom: '20px', fontWeight: '500' },
  ctaBtn: { backgroundColor: colors.rose, color: colors.white, textDecoration: 'none', padding: '14px 36px', fontSize: '14px', fontWeight: '600', fontFamily: fonts.body, letterSpacing: '0.5px', display: 'inline-block' },
  ctaTagline: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '12px', marginTop: '12px' },
};
